import React from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import ConfirmDialog from '../../components/ConfirmDialog';
import themebutton from '../../res/styles/theme_button.module.scss';
import LoadingWheel from '../../components/LoadingWheel';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import { USER_ROLES } from '../../res/Constants';
import { getCurrentRole, isCurrentUserAdmin } from '../../service/AccountService';
import { deleteResource, getResources } from '../../service/ResourceService';
import { toastError, toastSuccess } from '../../ui/ToastHelper';
import ResourceFilterBar from './components/ResourceFilterBar';
import ResourceTable from './components/ResourceTable';

const DEFAULT_SORT_FIELD = 'title';
const DEFAULT_SORT_DIRECTION = 'asc';

export default function ResourceList() {
    const history = useHistory();
    const queryClient = useQueryClient();

    const [searchText, setSearchText] = React.useState('');
    const [typeFilter, setTypeFilter] = React.useState('ALL');
    const [sortField, setSortField] = React.useState(DEFAULT_SORT_FIELD);
    const [sortDirection, setSortDirection] = React.useState(DEFAULT_SORT_DIRECTION);
    const [deleteTarget, setDeleteTarget] = React.useState(null);

    const { data: resources, isFetching } = useQuery({
        queryKey: ['resources'],
        queryFn: getResources,
        staleTime: Infinity,
        gcTime: Infinity
    });

    const filteredAndSorted = React.useMemo(() => {
        if (!resources) return [];

        const filtered = resources
            .filter((r) => typeFilter === 'ALL' || r.type === typeFilter.toLowerCase())
            .filter((r) => {
                if (!searchText) return true;
                const q = searchText.toLowerCase();
                return (
                    r.title.toLowerCase().includes(q) ||
                    r.tags.some((t) => t.name.toLowerCase().includes(q))
                );
            });

        return [...filtered].sort((a, b) => {
            let valA, valB;
            if (sortField === 'title') {
                valA = a.title;
                valB = b.title;
            } else if (sortField === 'createdAt') {
                valA = a.createdAt;
                valB = b.createdAt;
            }
            const cmp = typeof valA === 'string' ? valA.localeCompare(valB) : valA - valB;
            return sortDirection === 'asc' ? cmp : -cmp;
        });
    }, [resources, searchText, typeFilter, sortField, sortDirection]);

    if (getCurrentRole() === USER_ROLES.client) {
        return <Redirect to='/techforms' />;
    }

    const isAdmin = isCurrentUserAdmin();

    const handleSortChange = (field, direction) => {
        setSortField(field);
        setSortDirection(direction);
    };

    const handleAddClick = () => {
        history.push('/resources/add');
    };

    const handleEditClick = (resource) => {
        history.push(`/resources/edit/${resource.id}`, { resource });
    };

    const handleDeleteClick = (resource) => {
        setDeleteTarget(resource);
    };

    const handleDeleteConfirm = async (_, confirmed) => {
        if (confirmed) {
            try {
                await deleteResource(deleteTarget.id);
                queryClient.invalidateQueries({ queryKey: ['resources'] });
                toastSuccess('Resource deleted.');
            } catch {
                toastError('An error occurred while deleting the resource.');
            }
        }
        setDeleteTarget(null);
    };

    return (
        <PageWrapper>
            <NavigationBar />
            <ResourceFilterBar
                onSearchChange={setSearchText}
                onTypeFilterChange={setTypeFilter}
                isAdmin={isAdmin}
                onAddClick={handleAddClick}
            />
            {isFetching ? (
                <LoadingContainer>
                    <LoadingWheel isVisible centerMode='absolute' height={120} width={120} />
                </LoadingContainer>
            ) : (
                <ResourceTable
                    resources={filteredAndSorted}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                    isAdmin={isAdmin}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                />
            )}

            <ConfirmDialog
                isOpen={deleteTarget !== null}
                text={`Delete resource?\nThis action cannot be undone.`}
                elementId={deleteTarget?.id ?? 0}
                onConfirm={handleDeleteConfirm}
                cancelClassName={themebutton.gray}
                confirmClassName={themebutton.red}
                confirmText="DELETE"
            />
        </PageWrapper>
    );
}

const PageWrapper = styled.div`
    min-height: 100vh;
    background-color: #f5f5f5;
`;

const LoadingContainer = styled.div`
    position: relative;
    height: 60vh;
`;
